import * as React from "react";
import {useState, useEffect, useRef} from "react";
import BtcRingLogo from './BtcRingLogo';
import BlockTimeline3D from './BlockTimeline3D';
import axios from "axios";
import moment from "moment";

const HOST_PORT = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:3003'
  : 'https://api.blockhivetechnologies.com';

const ETH_CHAIN_CONFIGS = [
  { key: 'eth',  rpc: 'https://eth.drpc.org',                    color: '#627EEA', label: 'L1',     maxBlocks: 30  },
  { key: 'base', rpc: 'https://mainnet.base.org',                color: '#0052FF', label: 'BASE',   maxBlocks: 150 },
  { key: 'arb',  rpc: 'https://arb1.arbitrum.io/rpc',            color: '#28A0F0', label: 'ARB',    maxBlocks: 500 },
  { key: 'op',   rpc: 'https://mainnet.optimism.io',             color: '#FF0420', label: 'OP',     maxBlocks: 150 },
  { key: 'monad', rpc: 'https://rpc.monad.xyz',                  color: '#836EF9', label: 'MON',    maxBlocks: 600 },
];

// Fallback ISP data — shown instantly while API loads in background
const LIGHTNING_FALLBACK = {
  clearnet: '5015.4', tor: '113.5', unknown: '563.4',
  isps: [
    { name: 'Amazon.com',      capacity_btc: '1967.9', nodes: 7957 },
    { name: 'Hetzner Online',  capacity_btc: '378.2',  nodes: 2926 },
    { name: 'Google Cloud',    capacity_btc: '201.3',  nodes: 1834 },
    { name: 'Frantech',        capacity_btc: '89.4',   nodes: 1102 },
    { name: 'Mullvad',         capacity_btc: '76.1',   nodes: 980  },
  ],
  countries: [
    { flag: '🇺🇸', name: 'United States',  count: 2630 },
    { flag: '🇩🇪', name: 'Germany',        count: 1822 },
    { flag: '🇫🇷', name: 'France',         count:  702 },
    { flag: '🇳🇱', name: 'Netherlands',    count:  598 },
    { flag: '🇬🇧', name: 'United Kingdom', count:  521 },
    { flag: '🇨🇦', name: 'Canada',         count:  418 },
  ],
};

const isoToFlag = (iso) =>
  String.fromCodePoint(...[...iso.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65));

const Tooltip = (props) => {
  const timeout = useRef(null);
  const [active, setActive] = useState(false);

  const showTip = () => {
    clearTimeout(timeout.current);
    timeout.current = setTimeout(() => {
      setActive(true);
    }, props.delay || 200);
  };

  const hideTip = () => {
    clearTimeout(timeout.current);
    timeout.current = null;
    setActive(false);
  };

  return (
    <div
      className="Tooltip-Wrapper"
      // When to show the tooltip
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
    >
      {/* Wrapping */}
      {props.children}
      {active && (
        <div className={`Tooltip-Tip ${props.direction || "bottom"}`}>
          {/* Content */}
          {props.content}
        </div>
      )}
    </div>
  );
};


/**
 * Main class
 */
class Blockclock extends React.Component {
  state = {
    blocks: [],
    hype_blocks: [],
    hype_latest: null,
    hype_window: 1000,
    timeline: [],
    timeline_start: moment().subtract(1, 'hour').format("h:mm a"),
    timeline_end: moment().format("h:mm:ss a"),
    error: '',
    hours: 2,
    connection_message: 'CONNECTING...',
    btc_price: '',
    hash_rate: '',
    avg_tx_fee: null,
    stacks_microblocks: '',
    eth_chains: {
      eth:  { blocks: [], latest: null },
      base: { blocks: [], latest: null },
      arb:  { blocks: [], latest: null },
      op:   { blocks: [], latest: null },
      monad: { blocks: [], latest: null },
    },
    eth_expanded: false,
    lightning: LIGHTNING_FALLBACK,
    lightning_expanded: false,
  }

  momentNow = this.props.mockTime ? moment(this.props.mockTime) : moment();
  _mounted = false;
  _visible = !document.hidden;

  // Pause all polling while the tab is hidden (~22K RPC req/hr otherwise),
  // then catch up immediately on wake
  handleVisibility = () => {
    const was = this._visible;
    this._visible = !document.hidden;
    if (!was && this._visible && this._mounted) {
      this.loadBlocks();
      this.updateStats();
      this.updateStacksStats();
      this.fetchLightning();
      this.loadHypeBlocks();   // re-fetch history — blocks were missed while hidden
      this.loadEthChains();
    }
  };
  blockPollingTimer = null;
  hypePollingTimer = null;
  ethChainsTimer = null;
  ethArbTimer = null;
  mempoolWs = null;
  lightningTimer = null;
  checkBlocksTimer = null;

  connectMempoolWs() {
    if (this.mempoolWs) return;
    const ws = new WebSocket('wss://mempool.space/api/v1/ws');
    ws.onopen = () => ws.send(JSON.stringify({ action: 'want', data: ['blocks'] }));
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.block?.difficulty) {
          // hashrate = difficulty * 2^32 / target_block_time(600s) → EH/s
          const hashRate = (msg.block.difficulty * Math.pow(2, 32)) / 600 / 1e18;
          this.setState({ hash_rate: `${hashRate.toFixed(2)} EH/s` });
        }
      } catch (e) { /* silent */ }
    };
    ws.onclose = () => {
      this.mempoolWs = null;
      // Reconnect after 5s
      setTimeout(() => this.connectMempoolWs(), 5000);
    };
    this.mempoolWs = ws;
  }

  updateStats() {
    // Initial hashrate fetch via REST (WebSocket keeps it live after this)
    axios.get(`${HOST_PORT}/proxy/mempool/hashrate`, { timeout: 10000 })
      .then(response => {
        const hashRate = response.data.currentHashrate / 1e18;
        this.setState(() => ({
          hash_rate: `${hashRate.toFixed(2)} EH/s`
        }));
      })
      .catch(err => console.error('[blockclock] Failed to load hashrate:', err));

    // Fetch BTC price from blockchain.info
    axios.get('https://blockchain.info/ticker', { timeout: 10000 })
      .then(response => {
        const price = response.data.USD.last;
        this.setState(() => ({ btc_price: price }));
        // Recalculate avg tx fee in USD once price is known
        this.setState(prev => {
          if (prev.avg_tx_fee && prev.avg_tx_fee.sats) {
            const btc = prev.avg_tx_fee.sats / 1e8;
            return { avg_tx_fee: { ...prev.avg_tx_fee, usd: (btc * price).toFixed(4) } };
          }
          return null;
        });
      })
      .catch(err => console.error('[blockclock] Failed to load price:', err));

    // Fetch avg tx fee from mempool recommended fees
    axios.get(`${HOST_PORT}/proxy/mempool/fees`, { timeout: 10000 })
      .then(response => {
        const AVG_TX_VBYTES = 141;
        const sats = response.data.hourFee * AVG_TX_VBYTES;
        const btc = sats / 1e8;
        const price = this.state.btc_price;
        const usd = price ? (btc * price).toFixed(4) : null;
        this.setState({ avg_tx_fee: { sats, btc: btc.toFixed(6), usd } });
      })
      .catch(err => console.error('[blockclock] Failed to load fees:', err));
  }

  updateStacksStats() {
    axios.get('https://api.mainnet.hiro.so/extended/v1/microblock?limit=1', { timeout: 10000 })
      .then(response => {
        const microblock = response.data?.results?.[0];
        if (microblock?.block_height) {
          this.setState({ stacks_microblocks: microblock.block_height });
        }
      })
      .catch(err => console.error('[blockclock] Failed to load Stacks L2:', err));
  }

  updateTimes() {
    let tempMoment = this.momentNow;
    this.setState({
      timeline_start: tempMoment.subtract(1, 'hour').format("h:mm a"),
      timeline_end: tempMoment.format("h:mm:ss a")
    });
  }

  loadEthChains = async () => {
    await Promise.allSettled(ETH_CHAIN_CONFIGS.map(async (chain) => {
      try {
        const res = await axios.post(chain.rpc,
          { jsonrpc: '2.0', method: 'eth_getBlockByNumber', params: ['latest', false], id: 1 },
          { timeout: 8000 }
        );
        const b = res.data.result;
        if (!b) return;
        const block = { height: parseInt(b.number, 16), time: parseInt(b.timestamp, 16) * 1000 };
        this.setState(prev => ({
          eth_chains: {
            ...prev.eth_chains,
            [chain.key]: { blocks: [block], latest: block.height }
          }
        }));
      } catch (e) { console.error(`[blockclock] Failed to load ${chain.key}:`, e.message); }
    }));
  }

  pollEthChains = async (keys) => {
    if (!this._mounted) return;
    const chains = ETH_CHAIN_CONFIGS.filter(c => keys.includes(c.key));
    await Promise.allSettled(chains.map(async (chain) => {
      if (!this._mounted) return;
      try {
        const res = await axios.post(chain.rpc,
          { jsonrpc: '2.0', method: 'eth_getBlockByNumber', params: ['latest', false], id: 1 },
          { timeout: 4000 }
        );
        const b = res.data.result;
        if (!b || !this._mounted) return;
        const block = { height: parseInt(b.number, 16), time: parseInt(b.timestamp, 16) * 1000 };
        this.setState(prev => {
          const chainData = prev.eth_chains[chain.key];
          if (chainData.latest === block.height) return null;
          const newBlocks = [...chainData.blocks, block].slice(-chain.maxBlocks);
          return {
            eth_chains: {
              ...prev.eth_chains,
              [chain.key]: { blocks: newBlocks, latest: block.height }
            }
          };
        });
      } catch (e) { /* silent */ }
    }));
  }

  loadHypeBlocks = async () => {
    try {
      // Phase 1: fetch latest block only — show something immediately
      const latestRes = await axios.post('https://rpc.hyperliquid.xyz/evm',
        { jsonrpc: '2.0', method: 'eth_getBlockByNumber', params: ['latest', false], id: 0 },
        { timeout: 5000 }
      );
      const lb = latestRes.data.result;
      if (!lb) return;
      const latestNum = parseInt(lb.number, 16);
      const latestBlock = {
        height: latestNum,
        time: parseInt(lb.timestamp, 16) * 1000,
        txCount: lb.transactions.length
      };
      if (this._mounted) this.setState({ hype_blocks: [latestBlock], hype_latest: latestNum });

      // Phase 2: load 100-block history in background without blocking render
      const TARGET = 100;
      const BATCH = 20;
      const batchPromises = [];
      for (let start = latestNum - TARGET + 1; start <= latestNum; start += BATCH) {
        const end = Math.min(start + BATCH - 1, latestNum);
        const batch = [];
        for (let n = start; n <= end; n++) {
          batch.push({ jsonrpc: '2.0', method: 'eth_getBlockByNumber', params: [`0x${n.toString(16)}`, false], id: n });
        }
        batchPromises.push(axios.post('https://rpc.hyperliquid.xyz/evm', batch, { timeout: 10000 }));
      }

      const responses = await Promise.all(batchPromises);
      if (!this._mounted) return;
      let allBlocks = [];
      for (const res of responses) {
        const parsed = res.data.filter(r => r.result).map(r => ({
          height: parseInt(r.result.number, 16),
          time: parseInt(r.result.timestamp, 16) * 1000,
          txCount: r.result.transactions.length
        }));
        allBlocks = allBlocks.concat(parsed);
      }
      allBlocks.sort((a, b) => a.height - b.height);
      if (this._mounted) this.setState({ hype_blocks: allBlocks, hype_latest: latestNum });
    } catch (err) {
      console.error('[blockclock] Failed to load Hype blocks:', err.message);
    }
  }

  pollHypeBlocks = async () => {
    if (!this._mounted) return;
    try {
      const res = await axios.post('https://rpc.hyperliquid.xyz/evm',
        { jsonrpc: '2.0', method: 'eth_getBlockByNumber', params: ['latest', false], id: 0 },
        { timeout: 3000 }
      );
      if (!res.data.result || !this._mounted) return;
      const block = {
        height: parseInt(res.data.result.number, 16),
        time: parseInt(res.data.result.timestamp, 16) * 1000,
        txCount: res.data.result.transactions.length
      };
      this.setState(prev => {
        if (prev.hype_latest === block.height) return null;
        const newBlocks = [...prev.hype_blocks, block].slice(-1000);
        return { hype_blocks: newBlocks, hype_latest: block.height };
      });
    } catch (err) { /* silent */ }
  }

  checkBlocks() {
    this.setState((prev) => {
      const oldBlocks = prev.blocks;
      const cutoff = new Date().valueOf() - (2 * 60 * 60 * 1000);
      return {
        blocks: oldBlocks.filter(block => block.time > cutoff)
      }
    });
  }

  loadBlocks = async () => {
    const startTime = new Date().valueOf() - (60 * 60 * 1000 * this.state.hours);

    try {
      // Fetch first page of blocks
      let allBlocks = [];
      let response = await axios.get(`${HOST_PORT}/proxy/mempool/blocks`, { timeout: 12000 });
      allBlocks = response.data;

      // Get the last block height from first page
      let lastHeight = allBlocks[allBlocks.length - 1].height;

      // Fetch more pages only if the oldest block we have is still newer than our window
      while (allBlocks[allBlocks.length - 1].timestamp * 1000 > startTime) {
        response = await axios.get(`${HOST_PORT}/proxy/mempool/blocks?height=${lastHeight}`, { timeout: 12000 });
        if (response.data.length === 0) break;
        allBlocks = allBlocks.concat(response.data);
        lastHeight = response.data[response.data.length - 1].height;
      }

      // Deduplicate by height (pagination overlap can include the same block twice)
      const seen = new Set();
      allBlocks = allBlocks.filter(b => seen.has(b.height) ? false : seen.add(b.height));

      console.log(`[blockclock] Fetched ${allBlocks.length} total blocks from API`);

      const blocks = allBlocks.map(block => ({
        height: block.height,
        hash: block.id,
        time: block.timestamp * 1000
      }));

      const recentBlocks = blocks.filter(({time}) => time >= startTime);
      console.log(`[blockclock] Filtered to ${recentBlocks.length} blocks in ${this.state.hours}hr window`);
      console.log('[blockclock] Block times:', recentBlocks.map(b => new Date(b.time).toLocaleTimeString()));

      this.setState({
        blocks: recentBlocks,
        connection_message: 'CONNECTED'
      });
    } catch (err) {
      console.error('[blockclock] Failed to load historical blocks:', err);
      this.setState({connection_message: 'API ERROR'});
    }
  }

  fetchLightning = async () => {
    const CACHE_KEY = 'blockclock_lightning';
    const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

    // Show cached data instantly while backend refreshes in background
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, ts } = JSON.parse(cached);
        this.setState({ lightning: data });
        // Treat as stale if countries or ISPs are missing/empty so fallback data always loads
        const countriesOk = Array.isArray(data.countries) && data.countries.length > 0;
        const ispsOk = Array.isArray(data.isps) && data.isps.length > 0;
        if (countriesOk && ispsOk && Date.now() - ts < CACHE_TTL) return;
      }
    } catch (_) {}

    try {
      const [ispResult, countryResult] = await Promise.allSettled([
        axios.get('https://mempool.space/api/v1/lightning/nodes/isp-ranking', { timeout: 10000 }),
        axios.get(`${HOST_PORT}/proxy/bitnodes/countries`, { timeout: 15000 }),
      ]);

      const prev = this.state.lightning;

      let ispFields = { clearnet: prev.clearnet, tor: prev.tor, unknown: prev.unknown, isps: prev.isps };
      if (ispResult.status === 'fulfilled') {
        const { clearnetCapacity, torCapacity, unknownCapacity, ispRanking } = ispResult.value.data;
        ispFields = {
          clearnet: (clearnetCapacity / 1e8).toFixed(1),
          tor: (torCapacity / 1e8).toFixed(1),
          unknown: (unknownCapacity / 1e8).toFixed(1),
          isps: (ispRanking || [])
            .sort((a, b) => b[3] - a[3])
            .slice(0, 5)
            .map(r => ({ name: r[1], capacity_btc: (r[2] / 1e8).toFixed(1), nodes: r[3] })),
        };
      }

      let countries = prev.countries;
      if (countryResult.status === 'fulfilled') {
        const raw = countryResult.value.data;
        if (Array.isArray(raw) && raw.length > 0) {
          countries = raw.map(c => ({ flag: isoToFlag(c.iso), name: c.name, count: c.count }));
        }
      }

      const data = { ...ispFields, countries };
      this.setState({ lightning: data });
      try { localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() })); } catch (_) {}
    } catch (e) { console.error('[blockclock] Lightning fetch failed:', e.message); }
  }

  componentDidMount() {
    this._mounted = true;
    console.log('[blockclock] Fetching blocks from mempool.space');

    // Load cached lightning data instantly before async fetch
    try {
      const cached = localStorage.getItem('blockclock_lightning');
      if (cached) {
        const { data } = JSON.parse(cached);
        if (data) this.setState({ lightning: data });
      }
    } catch (_) {}

    this.loadBlocks();
    this.updateStats();
    this.updateStacksStats();
    this.loadHypeBlocks();
    this.loadEthChains();
    this.connectMempoolWs();
    this.fetchLightning();
    this.checkBlocksTimer = setInterval(() => this.checkBlocks(), 30 * 1000);

    document.addEventListener('visibilitychange', this.handleVisibility);

    // Block + stats: async but not latency-critical, plain setInterval is fine at 30s
    this.blockPollingTimer = setInterval(() => {
      if (document.hidden) return;
      this.loadBlocks();
      this.updateStats();
      this.updateStacksStats();
    }, 30000);

    // Lightning: refresh every 5 min
    this.lightningTimer = setInterval(() => {
      if (document.hidden) return;
      this.fetchLightning();
    }, 5 * 60 * 1000);

    // Hype: recursive setTimeout so calls never pile up; idle while hidden
    const pollHype = async () => {
      if (!this._mounted) return;
      if (!document.hidden) await this.pollHypeBlocks();
      if (this._mounted) this.hypePollingTimer = setTimeout(pollHype, 500);
    };
    this.hypePollingTimer = setTimeout(pollHype, 500);

    // ETH L2 chains (slower): recursive setTimeout
    const pollEthSlow = async () => {
      if (!this._mounted) return;
      if (!document.hidden) await this.pollEthChains(['eth', 'base', 'op', 'monad']);
      if (this._mounted) this.ethChainsTimer = setTimeout(pollEthSlow, 2000);
    };
    this.ethChainsTimer = setTimeout(pollEthSlow, 2000);

    // ARB (fast): recursive setTimeout so concurrent requests can't pile up
    const pollArb = async () => {
      if (!this._mounted) return;
      if (!document.hidden) await this.pollEthChains(['arb']);
      if (this._mounted) this.ethArbTimer = setTimeout(pollArb, 500);
    };
    this.ethArbTimer = setTimeout(pollArb, 500);
  }

  lastBlock = (blocks) => {
    if (blocks.length === 0) return null;
    return blocks[blocks.length - 1];
  }

  componentWillUnmount() {
    this._mounted = false;
    document.removeEventListener('visibilitychange', this.handleVisibility);
    clearInterval(this.checkBlocksTimer);
    clearInterval(this.blockPollingTimer);
    clearInterval(this.lightningTimer);
    clearTimeout(this.hypePollingTimer);
    clearTimeout(this.ethChainsTimer);
    clearTimeout(this.ethArbTimer);
    if (this.mempoolWs) { this.mempoolWs.onclose = null; this.mempoolWs.close(); }
  }

  datalinkMessageClass = (message) => {
    switch (message) {
      case 'CONNECTING...':
        return 'datalink-neutral';
      case 'API ERROR':
        return 'datalink-negative';
      case 'CONNECTED':
        return 'datalink-positive';
      default:
        console.warn(Error(`Switch fallthrough. Message: ${message}`));
        return 'datalink-negative';
    }
  }

  render = () => (
    <React.Fragment>
      <div className="blockclock">

        <div className={'blockclock-titlebar'}>
          <div style={{ lineHeight: 0, marginTop: '-7px', marginBottom: '-8px' }}><BtcRingLogo size={42} /></div>
          <div>{this.momentNow.format('MM/DD/YYYY')}</div>
          {/* avg txn hidden */}
          <div className={"hashrate"}>
            <span>
              Hash Rate: {this.state.hash_rate}
              {this.state.blocks.length >= 2 && (() => {
                const sorted = [...this.state.blocks].sort((a, b) => a.time - b.time);
                const diffs = sorted.slice(1).map((b, i) => b.time - sorted[i].time);
                const avgMs = diffs.reduce((s, d) => s + d, 0) / diffs.length;
                const avgMin = Math.floor(avgMs / 60000);
                const avgSec = Math.round((avgMs % 60000) / 1000);
                return ` · Avg: ${avgMin}m\u00a0${avgSec}s`;
              })()}
            </span>
          </div>
        </div>

        <div className={'info-row'}>
          <div className={"datalink"}>
            dRTC: <span
            className={this.datalinkMessageClass(this.state.connection_message)}>{this.state.connection_message}</span>
          </div>
{this.state.stacks_microblocks && <div>L2: {Number(this.state.stacks_microblocks).toLocaleString()}</div>}
          {this.state.hash_rate && (() => {
            const hr = parseFloat(this.state.hash_rate);
            if (!hr) return null;
            const kwhPerBtc = Math.round((hr * 1e6 * 25 / 1000 * 24) / (144 * 3.125));
            return <div><span style={{color:'#f7931a'}}>⚡</span> {kwhPerBtc.toLocaleString()} kWh/₿</div>;
          })()}
          {(this.lastBlock(this.state.blocks)) &&
          <div>Last Block <span style={{fontSize:'1.8em', lineHeight:0, verticalAlign:'-0.1em'}}>■</span> = {this.lastBlock(this.state.blocks).height.toLocaleString()}</div>}
          {/* lightning-expand-btn hidden */}
        </div>

        <BlockTimeline3D blocks={this.state.blocks} lightning={this.state.lightning} />
      </div>

      {/* LightningPanel hidden */}

      <div className="eth-ecosystem">
        <div className="eth-eco-header">
          <span className="eth-eco-title">ETH</span>
          <span className="eth-eco-window">60s window</span>
          <button
            className={`eth-expand-btn${this.state.eth_expanded ? ' expanded' : ''}`}
            onClick={() => this.setState(prev => ({ eth_expanded: !prev.eth_expanded }))}
          >{this.state.eth_expanded ? '▲' : '▼'}</button>
        </div>
        <EthEcosystem chains={this.state.eth_chains} expanded={this.state.eth_expanded} />
      </div>

      <div className="hype-clock">
        <div className="hype-clock-titlebar">
          <div className="hype-clock-title">HIP3 <span className="hype-live">● LIVE</span></div>
          <div className="hype-clock-meta">
            {this.state.hype_blocks.length > 0
              ? `#${this.state.hype_blocks[0].height.toLocaleString()} — #${this.state.hype_latest.toLocaleString()} · ${this.state.hype_window} block window`
              : 'LOADING...'}
          </div>
          <div className="hype-clock-right">
            {this.state.hype_blocks.length >= 2 && (() => {
              const recent = this.state.hype_blocks.slice(-10);
              const span = (recent[recent.length-1].time - recent[0].time) / 1000;
              const bps = span > 0 ? ((recent.length - 1) / span).toFixed(2) : '—';
              return <span>{bps} <span style={{fontSize:'9px',opacity:0.6}}>BLK/S</span></span>;
            })()}
          </div>
        </div>
        <div className="hype-clock-blocks">
          {this.state.hype_blocks.length > 0
            ? <HypeTimeline blocks={this.state.hype_blocks} windowSec={this.state.hype_window} />
            : <div style={{ color: '#888', fontSize: '0.8rem', padding: '8px 0' }}>Connecting to Hyperliquid...</div>}
        </div>
        <div className="hype-clock-footer">
          <button
            className={`hype-win-btn${this.state.hype_window === 100 ? ' active' : ''}`}
            onClick={() => this.setState({ hype_window: 100 })}
          >100</button>
          <button
            className={`hype-win-btn${this.state.hype_window === 1000 ? ' active' : ''}`}
            onClick={() => this.setState({ hype_window: 1000 })}
          >1000</button>
        </div>
      </div>
    </React.Fragment>
  )
}


const Timeline = () => {
  const [times, setTimes] = useState([])

  const makeTimes = () => {

    const timeNow = moment();
    const lastMark = moment().minutes(timeNow.minutes() - (timeNow.minutes() % 15));

    let temp_array = []

    let i = 0;
    while (i < 6) {
      lastMark.subtract(15, 'minutes');

      const minutesInTimeline = 120;
      const msDiff = moment().diff(lastMark);
      const offset = (((msDiff / 1000) / 60) / minutesInTimeline * 100);

      temp_array.push({
        moment: lastMark,
        offset,
        display: lastMark.format('hh:mm')
      });
      i++;
    }

    setTimes(temp_array.reverse());
  }

  const showMarketOpen = () => {
    if (moment().isoWeekday() > 6) return false;
    if (moment().isBefore(moment().hour(9).minute(30))) return false;
    if (moment().isAfter(moment().hour(11).minute(29))) return false;
    return true;
  }

  const marketOpenOffset = () => {
    const minutesInTimeline = 120;
    const msDiff = moment().diff(moment().hour(9).minute(30));
    //console.log('marketOpenOffset msDiff: ', msDiff);
    return 100 - (((msDiff / 1000) / 60) / minutesInTimeline * 100);
  }

  const showMarketClose = () => {
    if (moment().isoWeekday() > 6) return false;
    if (moment().isBefore(moment().hour(16).minute(0))) return false;
    if (moment().isAfter(moment().hour(17).minute(59))) return false;
    return true;
  }

  const marketCloseOffset = () => {
    const minutesInTimeline = 120;
    const msDiff = moment().diff(moment().hour(16).minute(0));
    const hourDiff = (msDiff / 1000) / 60
    // console.log('marketCloseOffset hourDiff: ', hourDiff);
    return 100 - (100 * (hourDiff / minutesInTimeline));
  }

  useEffect(() => {
    makeTimes();
    const timesTimer = setInterval(() => makeTimes(), 1000);

    // onClose
    return (() => {
      clearInterval(timesTimer);
    })
  }, [])


  // console.log('showMarketOpen: ', showMarketOpen());
  // console.log('marketOpenOffset: ', marketOpenOffset());

  // console.log('showMarketClose: ', showMarketClose());
  // console.log('marketCloseOffset: ', marketCloseOffset());

  return (
    <div className={"timeline"}>
      <Time key="start" offset={100} display={moment().subtract(2, 'hours').format("h:mm:ss a")} iterator={'start'}/>
      {times.map((props, i) => <Time key={i} {...props} iterator={i}/>)}
      <Time key="end" offset={0} display={moment().format("h:mm:ss a")} iterator={'end'}/>
      {showMarketOpen() && <MarketOpen offset={marketOpenOffset()}/>}
      {showMarketClose() && <MarketClose offset={marketCloseOffset()}/>}
    </div>
  )
}

const MarketOpen = ({offset}) => {
  const componentStyle = (offset) => {
    return {
      left: `calc( ${offset}% - 10px )`
    };
  }

  return (
    <div
      className={'market-open'}
      style={componentStyle(offset)}
    />
  )
}

const MarketClose = ({offset}) => {
  const componentStyle = (offset) => {
    return {
      left: `calc( ${offset}% - 10px )`
    };
  }

  return (
    <div
      className={'market-close'}
      style={componentStyle(offset)}
    />
  )
}

const Time = ({display, offset, iterator}) => {
  const componentStyle = (offset) => {
    return {
      right: `calc( ${offset}% - 30px )`
    };
  }

  return (
    <div
      className={'timeValue'}
      style={componentStyle(offset)}
      key={iterator}
    >
            <span>
            {display}
            </span>
    </div>
  )
}

const Block = ({
                 iterator,
                 hash,
                 height,
                 time,
                 lightning,
               }) => {

  const rightOffset = (time) => {
    const minutesInTimeline = 120;
    const msDiff = moment().diff(moment(time));
    const offset = (((msDiff / 1000) / 60) / minutesInTimeline * 100);
    return `${offset}%`;
  }

  const componentStyle = (time) => {
    return {
      right: rightOffset(time),
      height: '28px',
    };
  }

  return (
    <div
      className="block"
      key={height}
      style={componentStyle(time)}
    >
            <span>
                <Tooltip content={
                  <div style={{ fontFamily: 'monospace', fontSize: '10px', width: '220px', whiteSpace: 'normal' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#aaa', marginBottom: '4px' }}>
                      <span>Block <span style={{ color: '#0ef8f8' }}>#{height}</span></span>
                      <span style={{ color: '#666' }}>{moment(time).format("hh:mm a")}</span>
                    </div>
                    {lightning && (<>
                      <div style={{ borderTop: '1px solid #333', margin: '4px 0' }}/>
                      <div style={{ color: '#f7931a', fontSize: '9px', fontWeight: 700, marginBottom: '4px', letterSpacing: '0.8px' }}>
                        ⚡ LIGHTNING NETWORK
                      </div>
                      <div style={{ display: 'flex', gap: '8px', fontSize: '9px', marginBottom: '5px' }}>
                        <span style={{ color: '#666' }}>Clearnet <span style={{ color: '#4CAF50' }}>{lightning.clearnet}</span></span>
                        <span style={{ color: '#666' }}>Tor <span style={{ color: '#58a6ff' }}>{lightning.tor}</span></span>
                        <span style={{ color: '#666' }}>Unk <span style={{ color: '#888' }}>{lightning.unknown}</span></span>
                      </div>
                      <div className="lightning-ticker-track">
                        <div className="lightning-ticker-inner">
                          {[...lightning.isps, ...lightning.isps].map((isp, i) => (
                            <span key={i}>
                              <span style={{ color: '#555' }}>{(i % lightning.isps.length) + 1} </span>
                              <span style={{ color: '#ccc' }}>{isp.name} </span>
                              <span style={{ color: '#f7931a' }}>{isp.capacity_btc} BTC </span>
                              <span style={{ color: '#666' }}>{isp.nodes}n</span>
                              <span style={{ color: '#444' }}> · </span>
                            </span>
                          ))}
                        </div>
                      </div>
                      {lightning.countries && lightning.countries.length > 0 && (<>
                        <div style={{ borderTop: '1px solid #333', margin: '5px 0 3px' }}/>
                        <div style={{ color: '#f7931a', fontSize: '9px', fontWeight: 700, marginBottom: '4px', letterSpacing: '0.8px' }}>
                          ₿ FULL NODES BY COUNTRY
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 8px' }}>
                          {lightning.countries.map((c, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '9px' }}>
                              <span style={{ fontSize: '11px' }}>{c.flag}</span>
                              <span style={{ color: '#999', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                              <span style={{ color: '#ccc', fontWeight: 700 }}>{c.count.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </>)}
                    </>)}
                  </div>
                } direction={"top"}>
                    <div className={'blockBody'}/>
                </Tooltip>
            </span>
    </div>
  )
}


const LightningPanel = ({ lightning }) => {
  if (!lightning) return null;
  return (
    <div className="lightning-panel">
      <div className="lightning-panel-row">
        <div className="lightning-panel-left">
          <span className="lightning-panel-title">⚡</span>
          <span className="lp-cap">Clearnet <strong>{lightning.clearnet}</strong></span>
          <span className="lp-cap">Tor <strong>{lightning.tor}</strong></span>
          <span className="lp-cap">Unk <strong>{lightning.unknown}</strong></span>
        </div>
        {lightning.isps && lightning.isps.length > 0 && (
          <div className="lightning-ticker-track lp-ticker">
            <div className="lightning-ticker-inner">
              {lightning.isps.map((isp, i) => (
                <span key={i}>
                  <span style={{ color: '#555' }}>{i + 1} </span>
                  <span style={{ color: '#ccc' }}>{isp.name} </span>
                  <span style={{ color: '#f7931a' }}>{isp.capacity_btc} BTC </span>
                  <span style={{ color: '#666' }}>{isp.nodes}n</span>
                  {i < lightning.isps.length - 1 && <span style={{ color: '#444' }}> · </span>}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      {lightning.countries && lightning.countries.length > 0 && (
        <div className="lightning-countries">
          {lightning.countries.map((c, i) => (
            <div key={i} className="lightning-country-row">
              <span className="lc-rank">{i + 1}</span>
              <span className="lc-flag">{c.flag}</span>
              <span className="lc-name">{c.name}</span>
              <span className="lc-count">{c.count.toLocaleString()}</span>
              <div className="lc-bar-wrap">
                <div
                  className="lc-bar"
                  style={{ width: `${Math.round((c.count / lightning.countries[0].count) * 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const HypeTimeline = ({ blocks, windowSec }) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden) setNow(Date.now()); }, 250);
    return () => clearInterval(t);
  }, []);

  const windowMs = windowSec * 1000;
  const cutoff = now - windowMs;
  const visible = blocks.filter(b => b.time >= cutoff);
  const maxTx = Math.max(1, ...visible.map(b => b.txCount));

  const rightPct = (blockTime) => {
    const ageMs = now - blockTime;
    return Math.max(0, Math.min(100, (ageMs / windowMs) * 100));
  };

  return (
    <div className="hype-blocks">
      {visible.map((block, i) => {
        const isNewest = i === visible.length - 1;
        const barH = Math.max(6, Math.round((block.txCount / maxTx) * 44) || 10);
        return (
          <div
            key={block.height}
            className={`hype-block-item${isNewest ? ' hype-newest' : ''}`}
            style={{ right: `${rightPct(block.time)}%` }}
          >
            <Tooltip
              content={
                <div className="block-info">
                  <table><tbody>
                    <tr><th>Block:</th><td>#{block.height.toLocaleString()}</td></tr>
                    <tr><th>Time:</th><td>{moment(block.time).format('hh:mm:ss a')}</td></tr>
                    <tr><th>Txs:</th><td>{block.txCount}</td></tr>
                  </tbody></table>
                </div>
              }
              direction="top"
            >
              <div className="hype-bar" style={{ height: `${barH}px` }} />
            </Tooltip>
          </div>
        );
      })}
    </div>
  );
};

const EthEcosystem = ({ chains, expanded }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden) setNow(Date.now()); }, 250);
    return () => clearInterval(t);
  }, []);

  const WINDOW_MS = 60 * 1000; // 60 second window
  const cutoff = now - WINDOW_MS;

  const calcAvgBlockTime = (blocks) => {
    if (blocks.length < 2) return null;
    const recent = blocks.slice(-6);
    const span = recent[recent.length - 1].time - recent[0].time;
    if (span <= 0) return null;
    return (span / ((recent.length - 1) * 1000)).toFixed(1);
  };

  const visibleChains = expanded ? ETH_CHAIN_CONFIGS : ETH_CHAIN_CONFIGS.filter(c => c.key === 'eth');

  return (
    <div className="eth-chains">
      {visibleChains.map(chain => {
        const data = chains[chain.key];
        const visible = data.blocks.filter(b => b.time >= cutoff);
        const avgBT = calcAvgBlockTime(data.blocks);
        const latest = data.latest;

        const rightPct = (blockTime) => {
          const ageMs = now - blockTime;
          return Math.max(0, Math.min(100, (ageMs / WINDOW_MS) * 100));
        };

        return (
          <div key={chain.key} className="eth-chain-row">
            <div className="eth-chain-label" style={{ color: chain.color }}>{chain.label}</div>
            <div className="eth-chain-block">#{latest ? latest.toLocaleString() : '—'}</div>
            <div className="eth-chain-speed" style={{ color: chain.color }}>
              {avgBT ? `${avgBT}s` : '—'}
            </div>
            <div className="eth-chain-timeline">
              {visible.map((block, i) => (
                <div
                  key={block.height}
                  className="eth-block-item"
                  style={{ right: `${rightPct(block.time)}%` }}
                >
                  <Tooltip
                    content={
                      <div className="block-info">
                        <table><tbody>
                          <tr><th>Block:</th><td>#{block.height.toLocaleString()}</td></tr>
                          <tr><th>Time:</th><td>{moment(block.time).format('hh:mm:ss a')}</td></tr>
                          <tr><th>Age:</th><td>{((now - block.time) / 1000).toFixed(1)}s ago</td></tr>
                        </tbody></table>
                      </div>
                    }
                    direction="top"
                  >
                    <div
                      className="eth-block-bar"
                      style={{
                        background: chain.color,
                        opacity: i === visible.length - 1 ? 1 : 0.6,
                        boxShadow: i === visible.length - 1 ? `0 0 4px ${chain.color}` : 'none',
                      }}
                    />
                  </Tooltip>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Blockclock;